import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiUrl } from "../../config/api";

const EMPTY_AREA_ROW = { territory: "", area: "" };
const createEmptyAreaRow = () => ({ ...EMPTY_AREA_ROW });
const areaRowHasContent = (row = {}) => {
  if (!row) return false;
  if (String(row.territory || "").trim()) return true;
  if (String(row.area ?? "").trim()) return true;
  return false;
};
const sanitizeAreaInput = (value) => String(value ?? "").replace(/[^0-9.,-]/g, "");
const parseAreaValue = (value) => {
  const cleaned = String(value ?? "").replace(/\s/g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : "";
};
const formatAreaValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value || "");
  return num.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
};
const textParser = (value) => String(value ?? "").trim();
const toInputString = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return String(value).replace(".", ",");
};
const sortObjectLinks = (list = []) => {
  return [...list].sort((a, b) => {
    const orderA = Number(a?.sortOrder) || 0;
    const orderB = Number(b?.sortOrder) || 0;
    if (orderA !== orderB) return orderA - orderB;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
};

// GeneralSheet — исправление: добавление строки работает всегда корректно!
// Теперь добавленная строка не исчезает после быстрого PATCH и получения новых данных.

export default function GeneralSheet({
  data,
  onChange,
  readOnlyLocked = false,
  objectLinks = [],
  objectLinksLoading = false,
  objectLinksError = '',
  onObjectLinksChange,
  onRequestLinksReload,
}) {
  // Локальный стейт всегда синхронизируется с props.data
  const [localData, setLocalData] = useState(data || {});
  const [objects, setObjects] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState("");
  const [objSearch, setObjSearch] = useState("");
  const [saved, setSaved] = useState(!!data?.objectCode);
  const [areaDraftRow, setAreaDraftRow] = useState(createEmptyAreaRow());
  const areaDraftRef = useRef(areaDraftRow);
  const [areaCellDraft, setAreaCellDraft] = useState({ idx: null, field: null, value: "" });
  const [linkForm, setLinkForm] = useState({ title: '', url: '' });
  const [linkSavingId, setLinkSavingId] = useState(null);
  const [linkDeletingId, setLinkDeletingId] = useState(null);
  const [linkError, setLinkError] = useState('');
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingDraft, setEditingDraft] = useState({ title: '', url: '' });

  useEffect(() => {
    let aborted = false;
    const token = typeof window !== "undefined" ? localStorage.getItem("authToken") || "" : "";
    fetch(apiUrl("/api/objects"), { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (res) => {
        const payload = await res.json().catch(() => null);
        if (aborted) return;
        if (res.ok && Array.isArray(payload)) {
          const sorted = [...payload].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ru"));
          setObjects(sorted);
        } else {
          setObjects([]);
        }
      })
      .catch(() => {
        if (!aborted) setObjects([]);
      });
    return () => {
      aborted = true;
    };
  }, []);

  useEffect(() => {
    if (!data?.objectId) return;
    setSelectedObjectId(String(data.objectId));
  }, [data?.objectId]);

  useEffect(() => {
    const oid = data?.objectId;
    if (!oid || !saved) return;
    let aborted = false;
    (async () => {
      try {
        const obj = await fetch(apiUrl(`/api/objects/${encodeURIComponent(oid)}`)).then(r=> r.ok ? r.json() : null).catch(()=>null);
        if (!obj || aborted) return;
        const next = { ...localData };
        const nextCompany = obj.customer ?? "";
        const nextAddress = obj.address ?? "";
        const needUpdate = (next.company !== nextCompany) || (next.address !== nextAddress);
        if (needUpdate) {
          next.company = nextCompany;
          next.address = nextAddress;
          setLocalData(next);
          // Обновляем карточку, если не заблокирована
          if (data.objectCode && onChange && !readOnlyLocked) onChange(next);
        }
      } catch {}
    })();
    return () => { aborted = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.objectId, saved, readOnlyLocked]);

  useEffect(() => {
    setLinkForm({ title: '', url: '' });
    setLinkError('');
    setEditingLinkId(null);
    setEditingDraft({ title: '', url: '' });
    setLinkSavingId(null);
    setLinkDeletingId(null);
  }, [data?.objectId]);

  // Сохранить новую карточку
  function handleSaveClick() {
    if (localData.objectCode) return;
    const objId = selectedObjectId ? Number(selectedObjectId) : null;
    if (!objId) {
      alert("Выберите объект");
      return;
    }
    // авто‑подстановка адреса и заказчика из выбранного объекта
    const obj = objects.find((o) => String(o.id) === String(objId));
    const prefill = {
      objectId: objId,
      objectName: obj?.name || "",
      address: obj?.address || "",
      company: obj?.customer || "",
      customer: localData.customer || "",
      periodYear: localData.periodYear || new Date().getFullYear(),
    };
    if (onChange) onChange({ ...localData, ...prefill });
    setSaved(true);
  }

  // Ввод названия отключён — объект выбирается из списка

  // Автосохранение для остальных полей
  function handleFieldChange(field, value) {
    const nextData = { ...localData, [field]: value };
    setLocalData(nextData);
    if (localData.objectCode && onChange && !readOnlyLocked) onChange(nextData);
  }

  // Таблица площадей
  const areaRows = Array.isArray(localData.areaTable) ? localData.areaTable : [];
  const canEditAreas = saved && !readOnlyLocked;
  const rowsWithDraft = canEditAreas ? [...areaRows, { ...areaDraftRow, _isDraft: true }] : areaRows;
  const currentObjectId = data?.objectId;
  const linksForView = sortObjectLinks(Array.isArray(objectLinks) ? objectLinks : []);
  const canViewLinks = Boolean(currentObjectId);
  const canManageLinks = canViewLinks && saved;

  const updateAreaRows = useCallback(
    (mutator, commitOpts) => {
      setLocalData((prev) => {
        const prevRows = Array.isArray(prev.areaTable) ? prev.areaTable : [];
        const nextRows = mutator(prevRows);
        const nextData = { ...prev, areaTable: nextRows };
        if (prev.objectCode && onChange && !readOnlyLocked) onChange(nextData, commitOpts);
        return nextData;
      });
    },
    [onChange, readOnlyLocked]
  );

  const handleAreaCellFocus = (idx, field, initialValue) => {
    if (!canEditAreas) return;
    setAreaCellDraft({ idx, field, value: initialValue });
  };

  const handleAreaInputChange = (idx, field, value) => {
    if (!canEditAreas) return;
    const nextVal = field === "area" ? sanitizeAreaInput(value) : value;
    setAreaCellDraft((prev) =>
      prev.idx === idx && prev.field === field
        ? { ...prev, value: nextVal }
        : { idx, field, value: nextVal }
    );
  };

  const commitAreaDraftRow = useCallback(
    (commitOpts) => {
      if (!canEditAreas) return;
      const current = areaDraftRef.current;
      if (!areaRowHasContent(current)) return;
      updateAreaRows(
        (rows) => [
          ...rows,
          {
            territory: textParser(current.territory),
            area: current.area === "" ? "" : current.area,
          },
        ],
        commitOpts
      );
      const empty = createEmptyAreaRow();
      areaDraftRef.current = empty;
      setAreaDraftRow(empty);
    },
    [canEditAreas, updateAreaRows]
  );

  const handleAreaCellBlur = (idx, field, parser = (v) => v, event) => {
    if (!canEditAreas) return;
    const isDraftRow = idx === areaRows.length;
    setAreaCellDraft((prev) => {
      if (prev.idx !== idx || prev.field !== field) return prev;
      const parsed = parser(prev.value);
      if (isDraftRow) {
        const current = areaDraftRef.current[field];
        if (current !== parsed) {
          setAreaDraftRow((draft) => {
            const next = { ...draft, [field]: parsed };
            areaDraftRef.current = next;
            return next;
          });
        }
      } else {
        const current = areaRows[idx]?.[field];
        if (current !== parsed) {
          updateAreaRows(
            (rows) => rows.map((row, i) => (i === idx ? { ...row, [field]: parsed } : row)),
            { forceCommit: true }
          );
        }
      }
      return { idx: null, field: null, value: "" };
    });

    if (isDraftRow) {
      const rowEl = event?.currentTarget?.closest?.("tr");
      const nextFocus = event?.relatedTarget;
      const stillInside =
        rowEl &&
        nextFocus &&
        typeof Node !== "undefined" &&
        nextFocus instanceof Node &&
        rowEl.contains(nextFocus);
      if (!stillInside && areaRowHasContent(areaDraftRef.current)) {
        commitAreaDraftRow({ forceCommit: true });
      }
    }
  };

  function handleRemoveAreaRow(idx) {
    if (!canEditAreas) return;
    updateAreaRows((rows) => rows.filter((_, i) => i !== idx), { forceCommit: true });
  }

  const handleLinkFormChange = (field, value) => {
    setLinkForm((prev) => ({ ...prev, [field]: value }));
  };

  async function handleCreateLink() {
    if (!canManageLinks || !currentObjectId) return;
    const title = String(linkForm.title || '').trim();
    const url = String(linkForm.url || '').trim();
    if (!title || !url) {
      setLinkError('Укажите название и ссылку');
      return;
    }
    setLinkSavingId('create');
    setLinkError('');
    try {
      const resp = await fetch(apiUrl(`/api/objects/${encodeURIComponent(currentObjectId)}/links`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, url }),
      });
      const payload = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(payload?.error || `HTTP ${resp.status}`);
      }
      if (typeof onObjectLinksChange === 'function') {
        onObjectLinksChange((prev = []) => {
          const base = Array.isArray(prev) ? prev : [];
          return sortObjectLinks([...base, payload]);
        });
      } else if (typeof onRequestLinksReload === 'function') {
        onRequestLinksReload(currentObjectId);
      }
      setLinkForm({ title: '', url: '' });
    } catch (e) {
      setLinkError(e?.message || 'Не удалось сохранить ссылку');
    } finally {
      setLinkSavingId(null);
    }
  }

  const startEditLink = (link) => {
    if (!canManageLinks) return;
    setEditingLinkId(link.id);
    setEditingDraft({ title: link.title || '', url: link.url || '' });
    setLinkError('');
  };

  const cancelEditLink = () => {
    setEditingLinkId(null);
    setEditingDraft({ title: '', url: '' });
  };

  async function handleEditLink() {
    if (!canManageLinks || !currentObjectId || !editingLinkId) return;
    const title = String(editingDraft.title || '').trim();
    const url = String(editingDraft.url || '').trim();
    if (!title || !url) {
      setLinkError('Укажите название и ссылку');
      return;
    }
    setLinkSavingId(editingLinkId);
    setLinkError('');
    try {
      const resp = await fetch(apiUrl(`/api/objects/${encodeURIComponent(currentObjectId)}/links/${encodeURIComponent(editingLinkId)}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, url }),
      });
      const payload = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(payload?.error || `HTTP ${resp.status}`);
      }
      if (typeof onObjectLinksChange === 'function') {
        onObjectLinksChange((prev = []) => {
          const base = Array.isArray(prev) ? prev : [];
          return sortObjectLinks(base.map((item) => (item.id === payload.id ? payload : item)));
        });
      } else if (typeof onRequestLinksReload === 'function') {
        onRequestLinksReload(currentObjectId);
      }
      cancelEditLink();
    } catch (e) {
      setLinkError(e?.message || 'Не удалось обновить ссылку');
    } finally {
      setLinkSavingId(null);
    }
  }

  async function handleDeleteLink(linkId) {
    if (!canManageLinks || !currentObjectId || !linkId) return;
    if (!window.confirm('Удалить ссылку?')) return;
    setLinkDeletingId(linkId);
    setLinkError('');
    try {
      const resp = await fetch(apiUrl(`/api/objects/${encodeURIComponent(currentObjectId)}/links/${encodeURIComponent(linkId)}`), {
        method: 'DELETE',
      });
      if (!resp.ok) {
        let errPayload = null;
        try { errPayload = await resp.json(); } catch { errPayload = null; }
        throw new Error(errPayload?.error || `HTTP ${resp.status}`);
      }
      if (typeof onObjectLinksChange === 'function') {
        onObjectLinksChange((prev = []) => {
          const base = Array.isArray(prev) ? prev : [];
          return base.filter((item) => item.id !== linkId);
        });
      } else if (typeof onRequestLinksReload === 'function') {
        onRequestLinksReload(currentObjectId);
      }
      if (editingLinkId === linkId) {
        cancelEditLink();
      }
    } catch (e) {
      setLinkError(e?.message || 'Не удалось удалить ссылку');
    } finally {
      setLinkDeletingId(null);
    }
  }

  const totalArea = areaRows.reduce((sum, row) => sum + (Number(row.area) || 0), 0);

  if (!localData) return <div>Загрузка...</div>;

  const filteredObjects = (objSearch || "").trim() === ""
    ? objects
    : objects.filter((o) =>
        String(o.name || "").toLowerCase().includes(objSearch.toLowerCase()) ||
        String(o.codeBase).includes(objSearch) ||
        String(Number(o.codeBase)).includes(objSearch)
      );

  const monthOptions = [
    { v: 1, t: "Январь" }, { v: 2, t: "Февраль" }, { v: 3, t: "Март" },
    { v: 4, t: "Апрель" }, { v: 5, t: "Май" }, { v: 6, t: "Июнь" },
    { v: 7, t: "Июль" }, { v: 8, t: "Август" }, { v: 9, t: "Сентябрь" },
    { v: 10, t: "Октябрь" }, { v: 11, t: "Ноябрь" }, { v: 12, t: "Декабрь" }
  ];
  const contractTypes = [
    { v: 'stationary', t: 'Стационар' },
    { v: 'one-time', t: 'Разовый' },
  ];

  return (
    <div>
      <div style={{
        fontWeight: "bold",
        fontSize: "1.2rem",
        textAlign: "center",
        marginBottom: 16
      }}>
        {(!localData.objectCode) ? (
          <div>
            <input
              placeholder="Поиск по коду или названию"
              value={objSearch}
              onChange={(e) => setObjSearch(e.target.value)}
              style={{ fontSize: "1rem", padding: "8px 10px", marginBottom: 6, borderRadius: 5, border: "1px solid #ccc", minWidth: 420 }}
            />
            <br />
            <select
              value={selectedObjectId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedObjectId(val);
                const obj = objects.find((o) => String(o.id) === String(val));
                setLocalData((s) => ({
                  ...s,
                  objectName: obj?.name || s.objectName,
                  address: obj?.address || s.address,
                  company: obj?.customer || s.company,
                }));
              }}
              style={{ fontSize: "1.1rem", padding: "8px 20px", marginBottom: 6, borderRadius: 5, border: "1px solid #ccc", minWidth: 420 }}
            >
              <option value="">Выберите объект…</option>
              {filteredObjects.map((o) => (
                <option key={o.id} value={o.id}>{Number(o.codeBase)} — {o.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <span>{localData.objectName}</span>
        )}
        {!saved && (
          <button
            onClick={handleSaveClick}
            style={{
              marginLeft: 18,
              fontSize: "1rem",
              padding: "6px 18px",
              borderRadius: 5,
              background: "#e7f6d4",
              border: "1px solid #8bc34a",
              cursor: "pointer"
            }}
            disabled={!localData.objectName || !localData.objectName.trim()}
          >
            Сохранить
          </button>
        )}
      </div>
      {/* Период расчёта — перенесён с объекта на карточку */}
      <div style={{ marginBottom: 11, display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ width: 110, textAlign: "right", fontWeight: 500 }}>Месяц:</label>
        <select
          value={localData.periodMonth || ""}
          onChange={e => handleFieldChange("periodMonth", e.target.value ? Number(e.target.value) : null)}
          disabled={!saved || readOnlyLocked}
          style={{ ...inputStyle, padding: "6px 10px" }}
        >
          <option value="">—</option>
          {monthOptions.map(m => (
            <option key={m.v} value={m.v}>{m.t}</option>
          ))}
        </select>
        <label style={{ width: 70, textAlign: "right", fontWeight: 500 }}>Год:</label>
        <input
          type="number"
          min={2000}
          max={2100}
          value={localData.periodYear || new Date().getFullYear()}
          onChange={e => handleFieldChange("periodYear", e.target.value ? Number(e.target.value) : null)}
          disabled={!saved || readOnlyLocked}
          style={inputStyle}
        />
      </div>
      {/* Вид контракта */}
      <div style={{ marginBottom: 11, display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ width: 110, textAlign: "right", fontWeight: 500 }}>Вид контракта:</label>
        <select
          value={localData.contractType || 'stationary'}
          onChange={e => handleFieldChange('contractType', e.target.value)}
          disabled={!saved || readOnlyLocked}
          style={{ ...inputStyle, padding: '6px 10px' }}
          title="Разовый — не может быть включён в группы. Стационар — может участвовать в группах."
        >
          {contractTypes.map(ct => (
            <option key={ct.v} value={ct.v}>{ct.t}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          Разовый — не включается в группы; Стационар — можно включать в группы
        </span>
      </div>
      <div style={{ marginBottom: 11, display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ width: 110, textAlign: "right", fontWeight: 500 }}>Заказчик:</label>
        <input
          type="text"
          value={localData.company || ""}
          readOnly
          title="Подтягивается из карточки объекта"
          style={{ ...inputStyle, background: '#f8fafc', flex: '1 1 0%', minWidth: 0 }}
        />
      </div>
      <div style={{ marginBottom: 11, display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ width: 110, textAlign: "right", fontWeight: 500 }}>Адрес:</label>
        <input
          type="text"
          value={localData.address || ""}
          readOnly
          title="Подтягивается из карточки объекта"
          style={{ ...inputStyle, background: '#f8fafc', flex: '1 1 0%', minWidth: 0 }}
        />
      </div>
      <div
        style={{
          marginBottom: 18,
          padding: '12px 14px',
          borderRadius: 10,
          border: '1px solid #e2e8f0',
          background: '#f8fafc',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Ссылки по объекту</div>
        {!canViewLinks ? (
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            Сохраните карточку и выберите объект, чтобы добавить полезные ссылки.
          </div>
        ) : (
          <>
            {objectLinksLoading ? (
              <div style={{ fontSize: 13, color: '#64748b' }}>Загрузка…</div>
            ) : objectLinksError ? (
              <div style={{ fontSize: 13, color: '#b91c1c' }}>{objectLinksError}</div>
            ) : linksForView.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {linksForView.map((link) => (
                  <div
                    key={link.id}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    {editingLinkId === link.id ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <input
                          type="text"
                          value={editingDraft.title}
                          onChange={(e) => setEditingDraft((prev) => ({ ...prev, title: e.target.value }))}
                          placeholder="Название"
                          style={{ ...inputStyle, flex: '1 1 220px', minWidth: 180 }}
                          disabled={linkSavingId === link.id}
                        />
                        <input
                          type="text"
                          value={editingDraft.url}
                          onChange={(e) => setEditingDraft((prev) => ({ ...prev, url: e.target.value }))}
                          placeholder="https://example.com"
                          style={{ ...inputStyle, flex: '1 1 260px', minWidth: 200 }}
                          disabled={linkSavingId === link.id}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            onClick={handleEditLink}
                            disabled={linkSavingId === link.id}
                            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #c4d7f5', background: '#e0edff', fontWeight: 600 }}
                          >
                            {linkSavingId === link.id ? 'Сохранение…' : 'Сохранить'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditLink}
                            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #f0d4d4', background: '#fff', color: '#c2410c' }}
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          style={{ color: '#1d4ed8', fontWeight: 600, textDecoration: 'none' }}
                          title={link.url}
                        >
                          {link.title || link.url}
                        </a>
                        <span style={{ color: '#64748b', fontSize: 12 }}>{link.url}</span>
                        {canManageLinks && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              type="button"
                              onClick={() => startEditLink(link)}
                              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5f5', background: '#eef2ff' }}
                            >
                              Изменить
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteLink(link.id)}
                              disabled={linkDeletingId === link.id}
                              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fecdd3', background: '#fff1f2', color: '#b91c1c' }}
                            >
                              {linkDeletingId === link.id ? 'Удаляем…' : 'Удалить'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>Для объекта пока нет ссылок.</div>
            )}
            {linkError && (
              <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 6 }}>{linkError}</div>
            )}
            {canManageLinks && (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Название"
                  value={linkForm.title}
                  onChange={(e) => handleLinkFormChange('title', e.target.value)}
                  style={{ ...inputStyle, flex: '1 1 220px', minWidth: 180 }}
                  disabled={linkSavingId === 'create'}
                />
                <input
                  type="text"
                  placeholder="https://example.com"
                  value={linkForm.url}
                  onChange={(e) => handleLinkFormChange('url', e.target.value)}
                  style={{ ...inputStyle, flex: '1 1 260px', minWidth: 200 }}
                  disabled={linkSavingId === 'create'}
                />
                <button
                  type="button"
                  onClick={handleCreateLink}
                  disabled={linkSavingId === 'create'}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #7dd3fc', background: '#e0f2fe', fontWeight: 600 }}
                >
                  {linkSavingId === 'create' ? 'Сохранение…' : 'Добавить ссылку'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: "bold", fontSize: "1rem", marginBottom: 4 }}>
          Площадь
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
          {canEditAreas
            ? "Последняя строка всегда пустая — начните ввод, и она сохранится автоматически."
            : saved
              ? "Карточка подписана — редактирование площадей недоступно."
              : "Сначала сохраните карточку, чтобы ввести площади."}
        </div>
        <div style={areaTableWrapper}>
          <table style={areaTableStyle}>
            <thead>
              <tr>
                <th style={areaHeaderCell}>Территория</th>
                <th style={{ ...areaHeaderCell, textAlign: "right" }}>Площадь, м²</th>
                {canEditAreas && <th style={{ ...areaHeaderCell, textAlign: "center", width: 90 }}>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {!canEditAreas && areaRows.length === 0 && (
                <tr>
                  <td colSpan={2} style={areaEmptyCell}>Нет сохранённых площадей</td>
                  {canEditAreas && <td />}
                </tr>
              )}

              {rowsWithDraft.map((row, idx) => {
                const isDraft = canEditAreas && idx === areaRows.length;
                const rowKey = isDraft ? "area-draft" : `area-${idx}`;
                const draftEmpty = isDraft && !areaRowHasContent(row);
                const editingField = areaCellDraft.idx === idx ? areaCellDraft.field : null;
                const territoryValue = editingField === "territory"
                  ? areaCellDraft.value
                  : (row.territory || "");
                const areaValue = editingField === "area"
                  ? areaCellDraft.value
                  : (row.area === "" || row.area === null || row.area === undefined ? "" : formatAreaValue(row.area));

                return (
                  <tr key={rowKey}>
                    <td style={areaCell}>
                      <input
                        type="text"
                        value={territoryValue}
                        disabled={!canEditAreas}
                        onFocus={(e) => {
                          if (!canEditAreas) return;
                          e.target.select();
                          handleAreaCellFocus(idx, "territory", row.territory || "");
                        }}
                        onChange={(e) => handleAreaInputChange(idx, "territory", e.target.value)}
                        onBlur={(e) => handleAreaCellBlur(idx, "territory", textParser, e)}
                        style={canEditAreas ? areaInputText : areaInputDisabled}
                      />
                    </td>
                    <td style={areaCellRight}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={areaValue}
                        disabled={!canEditAreas}
                        onFocus={(e) => {
                          if (!canEditAreas) return;
                          e.target.select();
                          handleAreaCellFocus(idx, "area", toInputString(row.area));
                        }}
                        onChange={(e) => handleAreaInputChange(idx, "area", e.target.value)}
                        onBlur={(e) => handleAreaCellBlur(idx, "area", parseAreaValue, e)}
                        style={canEditAreas ? areaInputRight : areaInputDisabled}
                      />
                    </td>
                    {canEditAreas && (
                      <td style={areaActionCell}>
                        {!isDraft && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAreaRow(idx)}
                            style={areaDeleteBtn}
                            title="Удалить строку"
                          >
                            🗑️
                          </button>
                        )}
                        {isDraft && draftEmpty && <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>}
                      </td>
                    )}
                  </tr>
                );
              })}

              <tr>
                <td style={areaSummaryCell}>Итого:</td>
                <td style={{ ...areaSummaryCell, textAlign: "right" }}>{formatAreaValue(totalArea)}</td>
                {canEditAreas && <td style={areaActionCell}></td>}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ marginBottom: 11, display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ width: 110, textAlign: "right", fontWeight: 500 }}>Наше юр.лицо:</label>
        <input
          type="text"
          value={localData.customer || ""}
          onChange={e => handleFieldChange("customer", e.target.value)}
          disabled={!saved || readOnlyLocked}
          style={{ ...inputStyle, flex: '1 1 0%', minWidth: 0 }}
        />
      </div>
      <div style={{ marginBottom: 5, display: "flex", alignItems: "center", gap: "12px" }}>
        <label style={{ width: 110, textAlign: "right", fontWeight: 500 }}>Комментарий:</label>
        <input
          type="text"
          value={localData.comment || ""}
          onChange={e => handleFieldChange("comment", e.target.value)}
          disabled={!saved || readOnlyLocked}
          style={{ ...inputStyle, flex: '1 1 0%', minWidth: 0 }}
        />
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "5px 12px",
  borderRadius: 4,
  border: "1px solid #ccc",
  fontSize: "1rem",
  background: "#fff"
};

const areaTableWrapper = {
  background: "var(--bg-card, #fff)",
  borderRadius: 18,
  border: "1px solid var(--border-200, #e6e8ef)",
  boxShadow: "var(--shadow-1, 0 18px 36px rgba(15,23,42,0.08))",
  overflow: "hidden",
  overflowX: "auto",
};

const areaTableStyle = {
  borderCollapse: "collapse",
  width: "100%",
  tableLayout: "fixed",
  fontSize: "0.95rem",
};

const areaHeaderCell = {
  padding: "10px 12px",
  borderBottom: "1px solid var(--border-200, #e6e8ef)",
  background: "linear-gradient(135deg, var(--brand-50, #f5efff), #f3f4f8)",
  fontWeight: 700,
  textAlign: "left",
  whiteSpace: "normal",
  lineHeight: 1.3,
};

const areaCell = {
  padding: "8px 12px",
  borderBottom: "1px solid var(--border-200, #e6e8ef)",
  background: "#fff",
  textAlign: "left",
  verticalAlign: "middle",
};

const areaCellRight = { ...areaCell, textAlign: "right" };
const areaActionCell = { ...areaCell, textAlign: "center", width: 100 };
const areaSummaryCell = { ...areaCell, fontWeight: 700 };
const areaEmptyCell = { ...areaCell, textAlign: "center", color: "#94a3b8" };

const areaInputBase = {
  width: "100%",
  padding: "6px 2px 4px",
  border: "none",
  borderBottom: "1px solid rgba(84,39,176,0.28)",
  background: "transparent",
  fontSize: "0.95rem",
  lineHeight: 1.25,
  boxSizing: "border-box",
};

const areaInputText = { ...areaInputBase, textAlign: "left" };
const areaInputRight = { ...areaInputBase, textAlign: "right" };
const areaInputDisabled = {
  ...areaInputBase,
  borderBottom: "1px solid #dfe3eb",
  color: "#94a3b8",
  cursor: "not-allowed",
};

const areaDeleteBtn = {
  border: "none",
  background: "none",
  color: "#b91c1c",
  cursor: "pointer",
  fontSize: 18,
  lineHeight: 1,
};